// Apolo Loop Engineering — LoopRunState (the "state file"): the single
// serializable source of truth for an agentic run. PURE: no React/IPC/fetch/D1.
import { createLedger, recordEdit, recordGreen, isStale } from '../oracle/greenLedger.js';
//
// Loop engineering principle: state is explicit and serializable, not implied by
// chat scrollback. The reducer is the ONLY transition function; observers/UI read
// state, never own it.

/**
 * @typedef {{n:number, action:'reason'|'act'|'gate', gateVerdict:('pass'|'fail'|'pending'|null), evidence:string[]}} LoopStep
 * @typedef {{runId:string, steps:LoopStep[], status:'running'|'awaiting_human'|'done'|'aborted'|'failed', cursor:any}} LoopRunState
 */

const STATUSES = ['running', 'awaiting_human', 'done', 'aborted', 'failed'];

/** @returns {LoopRunState} */
export function initLoopRunState(runId, cursor = null) {
  return { runId: String(runId || ''), steps: [], status: 'running', cursor };
}

/**
 * The ONLY transition function. Immutable: always returns a new object; unknown
 * events are a no-op (returns the same reference so callers can cheaply detect it).
 * @param {LoopRunState} state
 * @param {object} event
 * @returns {LoopRunState}
 */
export function reduceLoopRun(state, event) {
  if (!state || typeof state !== 'object') return state;
  if (!event || typeof event !== 'object') return state;
  switch (event.type) {
    case 'step': {
      const step = {
        n: state.steps.length + 1,
        action: event.action,
        gateVerdict: event.gateVerdict ?? null,
        evidence: Array.isArray(event.evidence) ? event.evidence.slice(0, 50) : [],
      };
      return {
        ...state,
        steps: [...state.steps, step],
        cursor: 'cursor' in event ? event.cursor : state.cursor,
      };
    }
    case 'status': {
      if (!STATUSES.includes(event.status)) return state;
      return { ...state, status: event.status };
    }
    case 'cursor':
      return { ...state, cursor: event.cursor };
    default:
      return state; // no-op
  }
}

/** Serialize to a stable JSON string (for persistence). */
export function serializeLoopRun(state) {
  return JSON.stringify(state);
}

/** Parse back; returns null on malformed input (never throws). */
export function deserializeLoopRun(str) {
  if (typeof str !== 'string' || !str) return null;
  try {
    const parsed = JSON.parse(str);
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.steps)) return null;
    return parsed;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// FC-1 — SPINE STATE (the 28 mutable accumulators in runLocalAgentLoopInner)
// ---------------------------------------------------------------------------
// Pure reducer over the iteration state. The loop body uses ONLY
// `applySpineEvent(s, event)` to mutate — no free `let` accumulators survive.
// Mutable containers (Map, Array) live INSIDE the state object; the reducer
// mutates in place (the spine owns the object — no concurrent readers) and
// returns the same reference for cheap identity checks.

/**
 * @typedef {object} SpineState
 * @property {any[]}    convo
 * @property {string}   finalText
 * @property {number}   iteration
 * @property {boolean}  executedAnyTool
 * @property {boolean}  wroteFile
 * @property {boolean}  committed
 * @property {boolean}  gatheredContext
 * @property {boolean}  flattenedToolHistoryMode
 * @property {number}   consecutiveToolErrors
 * @property {Map}      toolErrorsByName
 * @property {number}   repeatWarnings
 * @property {number|null} progressAtLastWarn
 * @property {boolean}  envRepairNudged
 * @property {string|null} lastContentSig
 * @property {number}   identicalContentRun
 * @property {number}   stallNudged
 * @property {boolean}  toolFailureNudged
 * @property {boolean}  commitNudged
 * @property {boolean}  greenClaimNudged
 * @property {boolean}  actionClaimNudged
 * @property {boolean}  replanNudged
 * @property {boolean}  truncationContinued
 * @property {number}   truncationRetries
 * @property {string|null} truncationKey
 * @property {number}   lastReminderIteration
 * @property {boolean}  ranChecksNudged
 * @property {boolean}  planNudged
 * @property {boolean}  contextNudged
 * @property {boolean}  auditNudged
 * @property {boolean}  generativeFastAnnounced
 * @property {number}   autoCorrectAttempts
 * @property {boolean}  autoCorrectGreen
 * @property {object|null} autoCorrectResult
 * @property {Map}      toolRepeatCounts
 * @property {any[]}    writtenFiles
 * @property {object}   greenLedger
 * @property {object}   turnTrace
 * @property {any[]}    ragChunks
 * @property {string[]} knownExistingPaths
 * @property {Map}      toolNameById
 * @property {Map}      toolArgsById
 * @property {object|null} lastRouteDecision
 */

/** @param {{ messages?: any[], turnTrace?: object, sessionId?: string, greenLedgerSeq?: number, greenLedgerFiles?: object }} cfg @returns {SpineState} */
export function initSpineState(cfg = {}) {
  return {
    convo: Array.isArray(cfg.messages) ? [...cfg.messages] : [],
    finalText: '',
    iteration: 0,
    executedAnyTool: false,
    wroteFile: false,
    committed: false,
    gatheredContext: false,
    flattenedToolHistoryMode: false,
    consecutiveToolErrors: 0,
    toolErrorsByName: new Map(), // D-11 — erros por tool no run (não zera em sucesso)
    repeatWarnings: 0,
    // T1 (fix/repeat-loop-overfire) — snapshot do "progresso" no último aviso de
    // repetição. Usado pra distinguir loop travado (0 progresso) de trabalho que
    // consulta a mesma ferramenta enquanto AVANÇA. null = ainda sem baseline.
    progressAtLastWarn: null,
    envRepairNudged: false,
    lastContentSig: null,
    identicalContentRun: 0,
    stallNudged: 0,
    toolFailureNudged: false,
    commitNudged: false,
    greenClaimNudged: false,
    actionClaimNudged: false,
    replanNudged: false,
    truncationContinued: false, // Wave-B — continuação de truncamento é BOUNDED (1×)
    truncationRetries: 0, // WS-E — truncagens consecutivas da mesma tool (args_truncated)
    truncationKey: null,  // WS-E — nome da tool da última truncagem (p/ detectar "consecutiva")
    lastReminderIteration: -1,
    ranChecksNudged: false,
    planNudged: false,
    contextNudged: false,
    auditNudged: false,
    generativeFastAnnounced: false,
    autoCorrectAttempts: 0,
    autoCorrectGreen: false,
    autoCorrectResult: null,
    toolRepeatCounts: new Map(),
    writtenFiles: [],
    // PR12 — reset-safe edit-vs-green ledger. Seeded from the persisted high-water
    // seq so a pre-resume edit can't masquerade as fresh (never mtime).
    greenLedger: createLedger({ sessionId: cfg.sessionId ?? null, seq: cfg.greenLedgerSeq, files: cfg.greenLedgerFiles }),
    turnTrace: cfg.turnTrace || {},
    ragChunks: [],
    knownExistingPaths: [],
    toolNameById: new Map(),
    toolArgsById: new Map(),
    // T04 (tier-s-sprint-unified-2026-06-28) — lastRouteDecision: the most
    // recent routeTurn() result, surfaced in the run timeline UI.
    lastRouteDecision: null,
  };
}

/**
 * The ONLY mutation path for the spine state. Handles typed events.
 * Mutates in place and returns the same reference (the spine owns it).
 * Unknown event types are a no-op (returns the same reference).
 * @param {SpineState} s
 * @param {object} event
 * @returns {SpineState}
 */
export function applySpineEvent(s, event) {
  if (!s || !event || typeof event !== 'object') return s;
  switch (event.type) {
    case 'tick':
      s.iteration += 1;
      return s;
    case 'set_final_text':
      s.finalText = event.text ?? '';
      return s;
    case 'tool_executed':
      s.executedAnyTool = true;
      // WS-E (review #5) — uma tool que EXECUTOU (args não truncados) quebra a sequência
      // de truncagens consecutivas. Sem isto, um build multi-arquivo onde o FileA se
      // recupera (skeleton+apply_edit) e depois o FileB trunca escalava no 1º truncamento
      // do FileB (retries ficava em 1 e o próximo virava 2) — matando um turno produtivo.
      s.truncationRetries = 0;
      s.truncationKey = null;
      return s;
    case 'flatten_mode':
      s.flattenedToolHistoryMode = true;
      return s;
    case 'tool_error':
      s.consecutiveToolErrors += 1;
      // D-11 pleno — erro por FERRAMENTA sobrevive a sucessos intermediários: a mesma
      // tool falhando 2+ no run vira lição ExpeL mesmo quando o run termina bem.
      if (event.name) s.toolErrorsByName.set(event.name, (s.toolErrorsByName.get(event.name) || 0) + 1);
      return s;
    case 'tool_success':
      s.consecutiveToolErrors = 0;
      return s;
    // E3-T03 — aviso do repeat-guard tem contador PRÓPRIO: não infla
    // consecutiveToolErrors (o spiral é pra erro REAL de tool, não repetição).
    // T1 (fix/repeat-loop-overfire) — GATE DE PROGRESSO: o aviso só escala pro
    // hard-stop quando o run NÃO avançou desde o último aviso. "Progresso" =
    // arquivo escrito OU assinatura NOVA (o modelo tentou algo diferente). Se
    // avançou, é trabalho — reinicia a contagem em 1 (o aviso atual ainda vale).
    // Se NÃO avançou, acumula. O loop patológico (só re-lê o MESMO arquivo, 0
    // progresso) tem progress constante → acumula → tripa cedo (readRepeatLoop).
    case 'repeat_warning': {
      const progress = s.writtenFiles.length + s.toolRepeatCounts.size;
      if (s.progressAtLastWarn != null && progress > s.progressAtLastWarn) {
        s.repeatWarnings = 1;
      } else {
        s.repeatWarnings += 1;
      }
      s.progressAtLastWarn = progress;
      return s;
    }
    // E12-T03 — ambiente quebrado: marca a chance única de reparo gasta (o erro
    // NÃO conta pro spiral nesta rodada; reincidência volta ao tool_error normal).
    case 'env_repair_nudge':
      s.envRepairNudged = true;
      return s;
    case 'content_check':
      if (event.noTools && event.sig) {
        s.identicalContentRun = event.sig === s.lastContentSig ? s.identicalContentRun + 1 : 1;
        s.lastContentSig = event.sig;
      } else {
        s.identicalContentRun = 0;
        s.lastContentSig = null;
      }
      return s;
    case 'nudge':
      if (event.which === 'stall') s.stallNudged += 1;
      else if (event.which === 'toolFailure') s.toolFailureNudged = true;
      else if (event.which === 'commit') s.commitNudged = true;
      else if (event.which === 'greenClaim') s.greenClaimNudged = true;
      else if (event.which === 'actionClaim') s.actionClaimNudged = true; // E14-T01
      else if (event.which === 'replan') s.replanNudged = true; // E15-T04
      else if (event.which === 'ranChecks') s.ranChecksNudged = true;
      else if (event.which === 'plan') s.planNudged = true;
      else if (event.which === 'context') s.contextNudged = true;
      else if (event.which === 'audit') s.auditNudged = true;
      return s;
    // E14-T03 — lembrete de sessão longa aplicado nesta iteração (1x por marco).
    case 'reminder_tick':
      s.lastReminderIteration = s.iteration;
      return s;
    case 'generative_fast_announced':
      s.generativeFastAnnounced = true;
      return s;
    case 'auto_correct_attempt':
      s.autoCorrectAttempts += 1;
      return s;
    case 'auto_correct_green':
      s.autoCorrectGreen = true;
      s.autoCorrectResult = event.result ?? null;
      // PR12 — the files written this turn just verified green: stamp their green seq.
      recordGreen(s.greenLedger, s.writtenFiles.map((f) => f.path).filter(Boolean));
      return s;
    case 'auto_correct_noop':
      s.autoCorrectGreen = true;
      return s;
    case 'tool_effects':
      if (event.gatheredContext) s.gatheredContext = true;
      if (event.wroteFile) s.wroteFile = true;
      if (event.committed) s.committed = true;
      if (event.writtenFile) {
        s.writtenFiles.push(event.writtenFile);
        // PR12 — record the edit (bumps the file's lastEditSeq on the logical clock).
        if (event.writtenFile.path) recordEdit(s.greenLedger, event.writtenFile.path);
      }
      if (event.knownPath) s.knownExistingPaths.push(event.knownPath);
      if (event.ragChunk) s.ragChunks.push(event.ragChunk);
      return s;
    case 'convo_push':
      if (event.message) s.convo.push(event.message);
      if (Array.isArray(event.messages)) {
        for (const m of event.messages) s.convo.push(m);
      }
      return s;
    case 'convo_replace':
      if (Array.isArray(event.convo)) s.convo = event.convo;
      return s;
    case 'tool_name':
      s.toolNameById.set(event.id, event.name);
      if (event.args !== undefined) s.toolArgsById.set(event.id, event.args);
      return s;
    case 'tool_repeat': {
      const count = (s.toolRepeatCounts.get(event.sig) || 0) + 1;
      s.toolRepeatCounts.set(event.sig, count);
      return s;
    }
    // WS-E (incidente tastedesign) — args truncados no cap de saída do provider.
    // Conta truncagens CONSECUTIVAS da mesma tool (reseta quando a tool muda) para
    // escalar em vez de re-nudge idêntico que faz o modelo re-emitir o arquivo inteiro.
    case 'args_truncated': {
      const key = String(event.key || '');
      if (s.truncationKey === key) s.truncationRetries += 1;
      else { s.truncationKey = key; s.truncationRetries = 1; }
      return s;
    }
    case 'route_decision': {
      // T04 — record the routeTurn result so the timeline can render it.
      // Pure: replaces the reference; no mutation of the event itself.
      s.lastRouteDecision = event.decision || null;
      return s;
    }
    default:
      return s;
  }
}

/**
 * PR12 — did the turn reach an HONEST green? True only when the oracle reached green
 * this turn AND (when ledger enforcement is on) no written file was edited after its
 * last green. Folds the two inline `s.autoCorrectResult && reachedGreen` checks the
 * done gate used, so the gate stays DP-flat. Enforcement OFF ⇒ identical to before.
 * @param {SpineState} s
 * @param {boolean} [ledgerEnforced=false]
 */
export function reachedHonestGreen(s, ledgerEnforced = false) {
  const green = !!(s.autoCorrectResult && s.autoCorrectResult.reachedGreen);
  return green && !(ledgerEnforced && isStale(s.greenLedger));
}

/**
 * Read-only snapshot of the spine state for return values.
 * @param {SpineState} s
 * @returns {{ iteration: number, finalText: string, executedAnyTool: boolean, autoCorrectResult: object|null }}
 */
export function snapshotSpineState(s) {
  return {
    iteration: s.iteration,
    finalText: s.finalText,
    executedAnyTool: s.executedAnyTool,
    autoCorrectResult: s.autoCorrectResult,
  };
}

export const _internals = { STATUSES };
